import { IWebsiteBuilderEditorHistoryActionBase, IWebsiteBuilderHistorySnapshot, WebsiteBuilderEditorActionType } from "../../websiteBuilderEditorAction";
import { IWebsiteBuilderRoot } from "pages/secure/facility/websiteBuilder/editor/nodes/root/websiteBuilderRoot";
import { IWebsiteBuilderHeader } from "pages/secure/facility/websiteBuilder/editor/nodes/section/websiteBuilderHeader";
import { IWebsiteBuilderFooter } from "pages/secure/facility/websiteBuilder/editor/nodes/section/websiteBuilderFooter";
import { isWebsiteBuilderElement, TWebsiteBuilderElement } from "pages/secure/facility/websiteBuilder/editor/nodes/element/websiteBuilderElement";
import { IWebsiteBuilderNodeDimensions, IWebsiteBuilderNodePosition } from "pages/secure/facility/websiteBuilder/editor/nodes/websiteBuilderNodeGeometry";
import { IWebsiteBuilderGeneralTraversalNode } from "../../actionHelpers/generalTraversal";
import { TWebsiteBuilderDevice } from "pages/secure/facility/websiteBuilder/editor/website/shared/device";

export interface IResizeElementActionPayload {
  id: string;
  device: TWebsiteBuilderDevice;
  prevPosition: IWebsiteBuilderNodePosition;
  nextPosition: IWebsiteBuilderNodePosition;
  prevDimensions: IWebsiteBuilderNodeDimensions;
  nextDimensions: IWebsiteBuilderNodeDimensions;
}

export interface IResizeElementAction extends IWebsiteBuilderEditorHistoryActionBase {
  type: typeof WebsiteBuilderEditorActionType.RESIZE_ELEMENT;
  payload: IResizeElementActionPayload;
}

export function getResizeElementInverseAction(action: IResizeElementAction): IResizeElementAction {
  const { id, device, nextPosition, prevPosition, nextDimensions, prevDimensions } = action.payload;

  return {
    type: WebsiteBuilderEditorActionType.RESIZE_ELEMENT,
    pageId: action.pageId,
    device: action.device,
    scrollNodeId: action.scrollNodeId,
    payload: {
      id,
      device,
      prevPosition: { ...nextPosition },
      nextPosition: { ...prevPosition },
      prevDimensions: { ...nextDimensions },
      nextDimensions: { ...prevDimensions },
    },
  };
}

type TResizeElementTraversalNode = TWebsiteBuilderElement | IWebsiteBuilderGeneralTraversalNode;

function resizeElementTraversal(
  header: IWebsiteBuilderHeader,
  root: IWebsiteBuilderRoot,
  footer: IWebsiteBuilderFooter,
  id: string,
  device: TWebsiteBuilderDevice,
  nextPosition: IWebsiteBuilderNodePosition,
  nextDimensions: IWebsiteBuilderNodeDimensions,
): IWebsiteBuilderHistorySnapshot {
  function traverse(node: TResizeElementTraversalNode): TResizeElementTraversalNode {
    if (isWebsiteBuilderElement(node)) {
      if (node.id === id) {
        return {
          ...node,
          element: {
            ...node.element,
            layout: {
              ...node.element.layout,
              [device]: {
                ...node.element.layout[device],
                position: nextPosition,
                dimensions: nextDimensions,
              },
            },
          },
        };
      } else {
        return node;
      }
    } else {
      if (!node.children) {
        return node;
      } else {
        return {
          ...node,
          children: node.children.map(traverse),
        };
      }
    }
  }

  return {
    header: traverse(header) as IWebsiteBuilderHeader,
    root: traverse(root) as IWebsiteBuilderRoot,
    footer: traverse(footer) as IWebsiteBuilderFooter,
  };
}

export function applyResizeElementChange(
  header: IWebsiteBuilderHeader,
  root: IWebsiteBuilderRoot,
  footer: IWebsiteBuilderFooter,
  action: IResizeElementAction,
): IWebsiteBuilderHistorySnapshot {
  const { id, device, nextPosition, nextDimensions } = action.payload;

  return resizeElementTraversal(
    header,
    root,
    footer,
    id,
    device,
    { ...nextPosition },
    { ...nextDimensions },
  );
}
